import { Permission } from './rolesResource';

export interface PermissionInfo {
  label: string;
  description: string;
}

export interface PermissionGroup {
  name: string;
  permissions: Permission[];
}

/**
 * Permission labels and descriptions for each permission type
 */
export const PERMISSION_LABELS: Record<Permission, PermissionInfo> = {
  login: {
    label: 'Login',
    description: 'Allow to log into the system.',
  },
  agent: {
    label: 'Manage Conversations',
    description:
      'Handle all customer interactions, including viewing profiles, sending messages, and assigning Tasks. View assigned Customer List and Search.',
  },
  liveboards: {
    label: 'View Liveboards',
    description: "See real-time dashboards of your contact center's performance and agent activity.",
  },
  'inboxes:viewAll': {
    label: 'View Inboxes',
    description: 'Access the contents of all Inboxes across teams.',
  },
  'reports:read': {
    label: 'View Reports',
    description: 'Access, filter, and download Reports and Dashboards.',
  },
  'answers:admin': {
    label: 'Manage Answers',
    description: 'Create and edit Answers, Help Centers, Audiences, Keyword Groups, and Languages for Answers.',
  },
  'topics:admin': {
    label: 'Manage Topics',
    description: 'Add, edit, or remove Topic options.',
  },
  'customer:dedicatedHero': {
    label: 'Assign Dedicated Hero',
    description: "Appoint a specific agent or Inbox as the primary contact for a customer's future interactions.",
  },
  'conversation:callCoach': {
    label: 'Coach Live Calls',
    description: 'Monitor ongoing phone calls, privately coach agents, and join the call.',
  },
  'conversation:callRecordingListen': {
    label: 'Listen to Voice Recordings',
    description: 'Hear Voicemails and listen to call recordings.',
  },
  'conversation:general': {
    label: 'Manage Conversations',
    description:
      'Handle customer interactions, including viewing profiles, creating Tasks and Notes, and assigning conversations and Tasks. View assigned Customer List and Search.',
  },
  'conversation:outbound': {
    label: 'Send Messages and Take Calls',
    description:
      'Send messages to customers through channels like Chat, Email, SMS, and Social. Make and answer phone calls.',
  },
  'conversation:read': {
    label: 'Read Conversations',
    description: 'Search and view customer profiles and conversations.',
  },
  'conversation:socialCommentModeration': {
    label: 'Social Content Moderation',
    description: 'Hide or delete social media content (comments) on connected accounts.',
  },
  analyst: {
    label: 'Analyst',
    description: 'Access and build reports.',
  },
  'answers:read': {
    label: 'View Answers',
    description: 'Read-only access to Answers and Help Centers.',
  },
  'api-user': {
    label: 'API User',
    description: 'Access to API endpoints.',
  },
  'compliance-admin': {
    label: 'Compliance Administrator',
    description: 'Administrative access to compliance features.',
  },
  developer: {
    label: 'Developer',
    description: 'Developer tools and capabilities.',
  },
  'system-admin': {
    label: 'Administrator',
    description: 'Full administrative access to the system.',
  },
  'task-user': {
    label: 'Task User',
    description: 'Interact with Tasks on the Customer Profile. Cannot respond to customers.',
  },
  'team-manager': {
    label: 'Team Manager',
    description: 'Team management capabilities.',
  },
};

/**
 * Core permissions that can be assigned to custom roles
 */
const CUSTOM_PERMISSIONS: Permission[] = [
  'answers:read',
  'answers:admin',
  'conversation:callCoach',
  'conversation:socialCommentModeration',
  'customer:dedicatedHero',
  'inboxes:viewAll',
  'liveboards',
  'topics:admin',
  'reports:read',
];

const LEGACY_PERMISSIONS: Permission[] = [
  'system-admin',
  'analyst',
  'api-user',
  'compliance-admin',
  'developer',
  'task-user',
  'team-manager',
];

export const SPLIT_AGENT_PERMISSIONS: Permission[] = [
  'conversation:callRecordingListen',
  'conversation:general',
  'conversation:outbound',
  'conversation:read',
];

/**
 * Get all permissions organized by groups
 */
export const getPermissionGroups = ({
  builtin,
  customRolesV2 = false,
}: {
  builtin: boolean;
  customRolesV2?: boolean;
}): PermissionGroup[] => {
  const customPermissions = [...CUSTOM_PERMISSIONS];

  if (customRolesV2) {
    customPermissions.push(...SPLIT_AGENT_PERMISSIONS);
  } else {
    customPermissions.push('agent');
  }

  customPermissions.sort();

  const groups: PermissionGroup[] = [
    {
      name: '',
      permissions: customPermissions,
    },
  ];

  if (builtin) {
    groups.push({
      name: 'Grouped Permissions',
      permissions: LEGACY_PERMISSIONS,
    });
  }

  return groups;
};
